import '.ab{position:relative;}.ssr-plugin'
import '.j2{width:2rem;}.ssr-plugin'
import '.kn{flex:0 0 auto;}.ssr-plugin'
import '.bi{position:absolute;}.ssr-plugin'
import '.ko{left:50%;}.ssr-plugin'
import '.kp{transform:translateX(-50%);}.ssr-plugin'
import '.kq{border-radius:99px;}.ssr-plugin'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { styled } from 'inlines'
import {
  STEP_PROGRESS_DOT_SIZE as DOT_SIZE,
  STEP_PROGRESS_GAP as GAP,
  STEP_PROGRESS_PILL_HEIGHT as PILL_HEIGHT,
  STEP_PROGRESS_PILL_WIDTH as PILL_WIDTH,
  stepProgressDotsHeight,
} from './stepProgressDotsHeight'

const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)'
const SPEED = 500
const RIPPLE = 6
const DECAY = 0.55

const containerHeight = stepProgressDotsHeight

const pillTop = (total: number) => (containerHeight(total) - PILL_HEIGHT) / 2

const dotTop = (index: number, current: number, total: number) => {
  const centeredPillTop = pillTop(total)

  if (index === current) {
    return centeredPillTop
  }

  if (index < current) {
    return centeredPillTop - (current - index) * (DOT_SIZE + GAP)
  }

  return (
    centeredPillTop +
    PILL_HEIGHT +
    GAP +
    (index - current - 1) * (DOT_SIZE + GAP)
  )
}

const dotBackground = (index: number, current: number) => {
  if (index === current) {
    return 'var(--brand-primary)'
  }

  if (index < current) {
    return 'var(--palette-red-300)'
  }

  return 'var(--brand-tertiary)'
}

const applyDotStyles = (
  el: HTMLDivElement,
  index: number,
  step: number,
  total: number,
  transition: string,
  topOffset = 0,
) => {
  const isPill = index === step

  el.style.transition = transition
  el.style.top = `${dotTop(index, step, total) + topOffset}px`
  el.style.width = `${isPill ? PILL_WIDTH : DOT_SIZE}px`
  el.style.height = `${isPill ? PILL_HEIGHT : DOT_SIZE}px`
  el.style.background = dotBackground(index, step)
}

const animateDots = (
  dots: HTMLDivElement[],
  toStep: number,
  total: number,
  ripple: boolean,
) => {
  const fullTransition = `top ${SPEED}ms ${SPRING}, width ${SPEED}ms ${SPRING}, height ${SPEED}ms ${SPRING}, background ${Math.round(SPEED * 0.5)}ms ease-out`
  const settleTransition = `top ${Math.round(SPEED * 1.1)}ms ${SPRING}`

  dots.forEach((el, index) => {
    let nudge = 0

    if (ripple && index > toStep) {
      const dist = index - toStep - 1
      nudge = RIPPLE * Math.pow(DECAY, dist)
    }

    if (nudge !== 0) {
      applyDotStyles(el, index, toStep, total, fullTransition, nudge)

      window.setTimeout(
        () => {
          el.style.transition = settleTransition
          el.style.top = `${dotTop(index, toStep, total)}px`
        },
        Math.round(SPEED * 0.2),
      )
    } else {
      applyDotStyles(el, index, toStep, total, fullTransition)
    }
  })
}

export const StepProgressDots = ({
  total,
  current,
  persistedStepIndexRef: persistedStepIndexRefProp,
}: {
  total: number
  current: number
  persistedStepIndexRef?: React.MutableRefObject<number | undefined>
}) => {
  const localPersistedStepIndexRef = useRef<number | undefined>(undefined)
  const persistedStepIndexRef =
    persistedStepIndexRefProp ?? localPersistedStepIndexRef
  const dotRefs = useRef<(HTMLDivElement | null)[]>([])
  const height = containerHeight(total)

  const setDotRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      dotRefs.current[index] = el
    },
    [],
  )

  useLayoutEffect(() => {
    if (total <= 0) {
      return
    }

    const dots = dotRefs.current.filter(Boolean)
    if (dots.length === 0) {
      return
    }

    const fromStep = persistedStepIndexRef.current ?? current

    if (fromStep === current) {
      dots.forEach((dot, index) => {
        applyDotStyles(dot, index, current, total, 'none')
      })
      persistedStepIndexRef.current = current
      return
    }

    dots.forEach((dot, index) => {
      applyDotStyles(dot, index, fromStep, total, 'none')
    })

    persistedStepIndexRef.current = current

    requestAnimationFrame(() => {
      animateDots(dots, current, total, current > fromStep)
    })
  }, [current, total, persistedStepIndexRef])

  if (total <= 0) {
    return null
  }

  return (
    <styled.div className="ab j2 kn"
      style={{
        height,}}
    >
      {Array.from({ length: total }, (_, index) => (
        <div className="bi ko kp kq"
          key={index}
          ref={setDotRef(index)}
        />
      ))}
    </styled.div>
  )
}
